<template>
	<MarkAsSeenButtonView
		:saving="savingStatus"
		:isMarked="isTitleSeen"
		@mark-as-seen-button-click="handleMarkAsSeen"
	/>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import MarkAsSeenButtonView from './MarkAsSeenButtonView.vue';

import { useListActions } from '@/helpers/composables/useListActions';
import { toastError } from '@/helpers/vfm/toast-helper';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { ObjectType } from '@/@types/graphql-types';
import { SeenListEvents } from '@/enums/events';

const TOAST_DURATION = 3000;

const props = withDefaults(
	defineProps<{
		titleId: string;
		titleObjectId: number;
		titleObjectType: ObjectType;
		titleName?: string;
		seasonNumber?: number | null;
		episodeNumber?: number | null;
		showSeenProgress?: number | null;
		isTitleSeen?: boolean;
		lightBackground?: boolean;
		saving?: boolean;
		useCustomEvent?: boolean;
		trackingAction?: string;
		showDetails?: {
			objectId: number;
			objectType: ObjectType;
		} | null;
	}>(),
	{
		titleName: '',
		trackingAction: 'watchlist',
	}
);

const emit = defineEmits(['markAsSeen']);

const { t } = useI18n();
const { setInSeenlist } = useListActions();

const localSaving = ref(false);

const savingStatus = computed(() => localSaving.value || props.saving);

function trackEvent(category: string) {
	const action = props.trackingAction;
	let value;

	if (props.titleObjectType === ObjectType.Show && props.showSeenProgress) {
		value = props.showSeenProgress < 100 ? 1 : 0;
	}

	const properties = { action, value };

	const contexts = [
		new SnowplowTitleContextGraphql(
			props.showDetails?.objectId ?? props.titleObjectId,
			props.showDetails?.objectType ?? props.titleObjectType,
			props.seasonNumber,
			props.episodeNumber
		),
	];

	TrackingHelper.trackEvent(category, properties, contexts);
}

async function handleMarkAsSeen() {
	if (!props.useCustomEvent) {
		if (savingStatus.value) return;

		localSaving.value = true;

		try {
			const category = props.isTitleSeen ? SeenListEvents.REMOVE : SeenListEvents.ADD;

			const episodeFields =
				props.seasonNumber && props.episodeNumber
					? {
							seasonNumber: props.seasonNumber,
							episodeNumber: props.episodeNumber,
					  }
					: {};

			trackEvent(category);

			await setInSeenlist(props.titleId, !!props.isTitleSeen, {
				objectType: props.titleObjectType,
				objectId: props.titleObjectId,
				title: props.titleName,
				...episodeFields,
			});
		} catch (e) {
			console.error('<MarkAsSeenButton> handleMarkAsSeen', e);
			toastError({ message: t('WEBAPP_SEENLIST_TOAST_ERROR'), duration: TOAST_DURATION });
		} finally {
			localSaving.value = false;
		}
	} else {
		emit('markAsSeen');
	}
}
</script>

<style scoped lang="scss"></style>
